import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useActiveCompany } from '@/lib/active-company-context';
import { useApi } from '@/lib/api-context';

import { impactResponseSchema, type ImpactRow, type RelationType } from './types';

// Datos de los documentos relacionados: el panorama (ambas direcciones) y el alta y
// baja de una relación. Cada cambio refresca el panorama y la lista (columna
// Relacionados).

const BASE = '/api/v1/quality/master-list';

export function useDocumentImpact(entryId: string, enabled: boolean): UseQueryResult<ImpactRow[]> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  return useQuery({
    queryKey: ['document-impact', activeCompanyId, entryId],
    queryFn: async () =>
      impactResponseSchema.parse(await api.get<unknown>(`${BASE}/${entryId}/impact`)).data,
    enabled: enabled && Boolean(activeCompanyId),
  });
}

function useRefreshRelations(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['document-impact'] }),
      qc.invalidateQueries({ queryKey: ['master-list'] }),
    ]);
  };
}

export function useRelate(
  entryId: string,
): UseMutationResult<void, Error, { targetEntryId: string; relationType: RelationType }> {
  const api = useApi();
  const refresh = useRefreshRelations();
  return useMutation({
    mutationFn: async (input) => {
      await api.post(`${BASE}/${entryId}/relations`, input);
    },
    // También tras un rechazo: otro usuario pudo crear o quitar la relación.
    onSettled: refresh,
  });
}

export function useUnrelate(entryId: string): UseMutationResult<void, Error, string> {
  const api = useApi();
  const refresh = useRefreshRelations();
  return useMutation({
    mutationFn: async (otherId) => {
      await api.delete(`${BASE}/${entryId}/relations/${otherId}`);
    },
    // También tras un rechazo: otro usuario pudo crear o quitar la relación.
    onSettled: refresh,
  });
}
